import { useEffect, useState } from 'react';
import { useRepositories } from '../../../infrastructure/http/RepositoryProvider';
import type { Device } from '../../../domain/Device';
import type { StationThresholds, ThresholdSection } from '../../../domain/Thresholds';

// Formulario de umbrales de una estación. Los valores se editan como texto
// (para poder borrar y tipear "2,5") y se convierten al guardar.
type Draft = Record<ThresholdSection, Record<string, string>>;

const toDraft = (t: StationThresholds): Draft =>
  Object.fromEntries(
    Object.entries(t).map(([section, values]) => [
      section,
      Object.fromEntries(Object.entries(values as Record<string, number>).map(([k, v]) => [k, String(v).replace('.', ',')])),
    ])
  ) as Draft;

export function useStationThresholds(deviceId: string) {
  const { devices: deviceRepo } = useRepositories();
  const [device, setDevice] = useState<Device | null>(null);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [defaults, setDefaults] = useState<StationThresholds | null>(null);
  const [custom, setCustom] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const [{ device: found }, response] = await Promise.all([deviceRepo.get(deviceId), deviceRepo.getThresholds(deviceId)]);
        setDevice(found);
        setDraft(toDraft(response.thresholds));
        setDefaults(response.defaults);
        setCustom(response.custom);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'No se pudieron cargar los umbrales.');
      } finally {
        setLoading(false);
      }
    })();
  }, [deviceId, deviceRepo]);

  const updateField = (section: ThresholdSection, key: string, value: string) => {
    setDraft((prev) => (prev ? { ...prev, [section]: { ...prev[section], [key]: value } } : prev));
    setSaved(false);
  };

  const handleSave = async (): Promise<boolean> => {
    if (!draft) return false;
    const parsed: Record<string, Record<string, number>> = {};
    for (const [section, values] of Object.entries(draft)) {
      parsed[section] = {};
      for (const [key, raw] of Object.entries(values)) {
        const value = Number(raw.replace(',', '.'));
        if (!raw.trim() || Number.isNaN(value)) {
          setError('Completá todos los valores con números.');
          return false;
        }
        parsed[section][key] = value;
      }
    }
    setSaving(true);
    setError(null);
    try {
      const response = await deviceRepo.updateThresholds(deviceId, parsed as unknown as StationThresholds);
      setDraft(toDraft(response.thresholds));
      setCustom(response.custom);
      setSaved(true);
      return true;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudieron guardar los umbrales.');
      return false;
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    setSaving(true);
    setError(null);
    try {
      const response = await deviceRepo.resetThresholds(deviceId);
      setDraft(toDraft(response.thresholds));
      setCustom(false);
      setSaved(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudieron restaurar los umbrales.');
    } finally {
      setSaving(false);
    }
  };

  return { device, draft, defaults, custom, loading, saving, error, saved, updateField, handleSave, handleReset };
}
